import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import setAlertValue from '@/components/alert';
import { validatorData } from '@/utils/culture-preference';
import { getLocalStorage, setLocalStorage } from '@/utils/storage';
import LoadingModal from '@/components/modal/LoadingModal';
import { trackClickEvent } from '@/utils/site-track';

export const STORAGE_NAME = 'cultural-quiz';

const FIX_ID = 'cultural_quiz_question';

interface Props {
  culturePreferenceText: { title: string; options: string[] }[];
  handleSave: (data: number[][]) => void;
  needToStorage?: boolean;
  dataEleGroup?: string;
}

const Question = (props: {
  text: string[];
  selectedValue: number[];
  onChange: (
    e: React.MouseEvent<HTMLParagraphElement>,
    value: number[],
  ) => void;
  dataEleGroup?: string;
}) => {
  const { text, selectedValue, onChange, dataEleGroup } = props;

  return (
    <div className="w-full flex justify-center gap-x-[96px] gap-y-8 flex-col items-center tablet:flex-row">
      <p
        data-ele-group={dataEleGroup}
        data-ele-id={`question_${text[0]}`}
        data-selected={selectedValue[0] === 1}
        onClick={(e) => onChange(e, [1, 0])}
        className="shrink-0 w-[350px] h-60 p-4 text-base leading-9 rounded-[30px] flex items-center justify-center bg-[#ddf6ff]/50 border border-transparent cursor-pointer data-[selected=true]:bg-[#40cdff] data-[selected=true]:border-[#22c4ff] hover:bg-[#ddf6ff] hover:border-[#22c4ff] data-[selected=true]:hover:bg-[#40cdff] tablet:w-[456px] tablet:h-[349px] tablet:text-lg tablet:leading-6.5"
      >
        {text[0]}
      </p>
      <p
        data-ele-group={dataEleGroup}
        data-ele-id={`question_${text[1]}`}
        data-selected={selectedValue[1] === 1}
        onClick={(e) => onChange(e, [0, 1])}
        className="shrink-0 w-[350px] h-60 p-4 text-base leading-9 rounded-[30px] flex items-center justify-center bg-[#ddf6ff]/50 border border-transparent cursor-pointer data-[selected=true]:bg-[#40cdff] data-[selected=true]:border-[#22c4ff] hover:bg-[#ddf6ff] hover:border-[#22c4ff] data-[selected=true]:hover:bg-[#40cdff] tablet:w-[456px] tablet:h-[349px] tablet:text-lg tablet:leading-6.5"
      >
        {text[1]}
      </p>
    </div>
  );
};

const QuizQuestion = (props: Props) => {
  const {
    handleSave,
    needToStorage = true,
    culturePreferenceText,
    dataEleGroup,
  } = props;

  const defaultFormData = Array.from(Array(culturePreferenceText.length), () =>
    Array(culturePreferenceText[0].options.length).fill(0),
  );
  const count = culturePreferenceText.length;

  const [formData, setFormData] = React.useState(cloneDeep(defaultFormData));
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const handleValidate = () => {
    const validateData = formData[selectedIndex];
    const result = validateData.includes(0) && validateData.includes(1);
    if (!result) {
      setAlertValue({ msg: '请选择一个合适的选项', severity: 'warning' });
    }
    return result;
  };

  const saveToStorage = (saveData: typeof defaultFormData) => {
    if (needToStorage) {
      const data = JSON.stringify(saveData);
      setLocalStorage(STORAGE_NAME, data);
    }
  };

  const goBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedIndex(selectedIndex - 1);
    dataEleGroup && trackClickEvent(e);
  };

  const handleViewResult = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (handleValidate()) {
      const result = formData.find(
        (item) => !(item.includes(1) || item.includes(0)),
      );
      if (selectedIndex !== count - 1 && !result) {
        setAlertValue({ msg: '提交参数不合法，请重试', severity: 'warning' });
        return;
      }
      saveToStorage(formData);
      setLoading(true);
      dataEleGroup && trackClickEvent(e);
      await handleSave(formData);
      setLoading(false);
    }
  };

  const handleSelect = (
    e: React.MouseEvent<HTMLParagraphElement>,
    value: number[],
    index: number,
  ) => {
    dataEleGroup && trackClickEvent(e);
    const newFormData = [...formData];
    newFormData.splice(index, 1, value);
    setFormData(newFormData);
    if (index < count - 1) {
      saveToStorage(newFormData);
      setSelectedIndex(selectedIndex + 1);
    }
  };

  React.useEffect(() => {
    if (needToStorage) {
      const data = getLocalStorage(STORAGE_NAME);
      const parseData = (data && JSON.parse(data)) || '';
      if (parseData && validatorData(parseData, culturePreferenceText)) {
        setFormData(parseData);
        const index = parseData.findIndex(
          (item: number[]) => item.toString() === defaultFormData[0].toString(),
        );
        setSelectedIndex(
          index === -1 ? count - 1 : index <= count - 1 ? index : count,
        );
      }
    }
  }, []);

  return (
    <>
      <div id={FIX_ID} className="w-full text-center max-w-[1000px] mx-auto">
        <p className="text-sm leading-5.5 mb-3 tablet:mb-4">
          {selectedIndex + 1}/{count}
        </p>
        <LinearProgress
          variant="determinate"
          value={Math.round(((selectedIndex + 1) * 100) / count)}
        />
        <p className="text-base mt-3 mb-8 tablet:text-2.75xl tablet:font-semibold tablet:mt-10 tablet:mb-16">
          下面哪一种说法更适合工作中的你？
        </p>
        <div className="mb-8">
          {culturePreferenceText.map(({ options, title }, index) => (
            <div
              className={`${index === selectedIndex ? 'block' : 'hidden'}`}
              key={title + index}
            >
              <Question
                text={options}
                selectedValue={formData[index]}
                onChange={(e, value) => handleSelect(e, value, index)}
                dataEleGroup={dataEleGroup}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-center items-center gap-4 flex-col tablet:flex-row">
          {selectedIndex > 0 && (
            <Button
              variant="outlined"
              color="inherit"
              className="w-[350px] !bg-slate-100 !px-10 !py-4 !border-none !text-sm !leading-5.5 tablet:w-auto tablet:rounded-full"
              onClick={(e) => goBack(e)}
              data-ele-id="back-button"
              data-ele-group={dataEleGroup}
            >
              上一道
            </Button>
          )}
          {selectedIndex === count - 1 && (
            <Button
              variant="outlined"
              color="inherit"
              className="w-[350px] !bg-slate-100 !px-10 !py-4 !border-none !text-sm !leading-5.5 tablet:w-auto tablet:!rounded-full"
              onClick={(e) => handleViewResult(e)}
              data-ele-id="view-result"
              data-ele-group={dataEleGroup}
            >
              查看测试结果
            </Button>
          )}
        </div>
      </div>
      <LoadingModal open={loading} />
    </>
  );
};

export default QuizQuestion;
